import { FilterWizardParser } from './filter-wizard-parser.js'
import { FilterStepOverview, FilterStepPagination, } from './typing/internal.js'
import { SEARCH_PRODUCTS_STORE, SearchProductsStore } from 'components/discovery/shared/search/stores/search-products-store.js'
import { FilterWizardConfig, FilterWizardFilters } from './typing/config.js'
import { AttributeValue, FilterAttributes } from 'components/discovery/shared/search/typing/filter-attributes.js'
import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createComputed, createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import {
  FILTERS_SEARCH_FILTERS_API,
  FiltersSearchAPI,
  FiltersSearchRequest
} from 'components/discovery/plp/filters/plp-filters/services/search-filters-api.js'
import { PlpFiltersSelectedFiltersEvent } from 'components/discovery/plp/filters/plp-filters/events.js'
import { FilterAttributesParser } from 'components/discovery/shared/search/parsers/filter-attributes-parser.js'
import { WINDOW_HISTORY, WindowHistory } from '@cloudsuite/mosaic-shared/scripts/window-history.js'

export const FILTER_WIZARD_STORE = Symbol('FILTER_WIZARD_STORE')

const defaultFilterWizardConfig: FilterWizardConfig = {
  name: '',
  description: '',
  type: 'tree',
  sortMode: 'priority',
  sortDirection: 'desc',
  navigationButtons: {
    previousVariant: 'primary',
    nextVariant: 'primary',
  },
  imageDisplay: {},
}

const defaultFilterWizardFilters: FilterWizardFilters = []

const defaultFilterStepPagination: FilterStepPagination = {
  activeStep: 0,
  hasPrevious: false,
  hasNext: true
}

export const PROPERTY_REF = 2

@registerDependency(FAKE, FILTER_WIZARD_STORE)
@registerDependency(REAL, FILTER_WIZARD_STORE)
export class FilterWizardStore {

  @inject(SEARCH_PRODUCTS_STORE)
  protected productsStore!: SearchProductsStore

  @inject(FILTERS_SEARCH_FILTERS_API)
  protected filtersAPI!: FiltersSearchAPI

  @inject(WINDOW_HISTORY)
  private history!: WindowHistory

  private parser: FilterWizardParser = new FilterWizardParser()
  private filterAttributesParser: FilterAttributesParser = new FilterAttributesParser()

  filterWizardConfig: () => FilterWizardConfig
  private setFilterWizardConfig: (value: FilterWizardConfig) => void

  filterWizardFilters: () => FilterWizardFilters
  private setFilterWizardFilters: (value: FilterWizardFilters) => void

  filterStepPagination: () => FilterStepPagination
  private setFilterStepPagination: (value: FilterStepPagination) => void

  filterStepOverview: () => FilterStepOverview[]

  searchIsLoading: () => boolean
  private setSearchIsLoading: (value: boolean) => void

  attributes: () => FilterAttributes

  treePath: string = ''

  private setFilterStepPaginationBasedOnActiveStep = (activeStep: number) => {
    this.setFilterStepPagination({
      activeStep: activeStep,
      hasPrevious: activeStep > 0,
      hasNext: this.filterWizardFilters().length - 1 > activeStep,
    })
  }

  private computeFilterStepOverview = (): FilterStepOverview[] => {
    return this.filterWizardFilters().map(filter => {
      const selectedValueIds: number[] = filter.attribute.valueIds
        .filter(valueId => this.attributes().getAttributeValue(filter.attribute.id, valueId).selected)

      return  {
        name: filter.name,
        attributeId: filter.attribute.id,
        hasSelection: selectedValueIds.length > 0,
        selectedValueIds: selectedValueIds,
      }
    })
  }

  constructor() {
    [this.filterWizardConfig, this.setFilterWizardConfig] = createSignal<FilterWizardConfig>(defaultFilterWizardConfig);
    [this.filterWizardFilters, this.setFilterWizardFilters] = createSignal<FilterWizardFilters>(defaultFilterWizardFilters);
    [this.filterStepPagination, this.setFilterStepPagination] = createSignal<FilterStepPagination>(defaultFilterStepPagination);
    [this.searchIsLoading, this.setSearchIsLoading] = createSignal<boolean>(false)

    this.attributes = createComputed<FilterAttributes>(this.productsStore.attributes)
    this.filterStepOverview = createComputed<FilterStepOverview[]>(this.computeFilterStepOverview)
  }

  initializeFilterWizard = async (treePath: string, configuration: any) => {
    this.treePath = treePath

    const parsed = this.parser.parseFilterWizardResponse(configuration)

    if (this.parser.configParseResultHasError(parsed)) {
      console.error(parsed.error)
      return
    }

    this.setFilterWizardConfig(parsed.config)
    this.productsStore.setAttributes(this.attributes().merge(parsed.attributes))
    this.setFilterWizardFilters(parsed.filters)
    this.setFilterStepPaginationBasedOnActiveStep(0)
  }

  emitCurrentFilterEvent = () => {
    this.emitFilterEvent(this.treePath)
  }

  previousStep = () => {
    const activeStep = this.filterStepPagination().activeStep - 1
    this.setFilterStepPaginationBasedOnActiveStep(activeStep)
  }

  nextStep = () => {
    const activeStep = this.filterStepPagination().activeStep + 1
    this.setFilterStepPaginationBasedOnActiveStep(activeStep)
  }

  gotoStep = (activeStep: number) => {
    this.setFilterStepPaginationBasedOnActiveStep(activeStep)
  }

  private fetchFilters = async (url: string) => {
    this.setSearchIsLoading(true)

    try {
      const request = new FiltersSearchRequest(url)
      const response = await this.filtersAPI.fetchFilters(request)

      if (response) {
        // Update browser URL with filter parameters
        this.history.replaceQueryFromPath(request.endpointUrl.toString())

        const parsedAttributes = this.filterAttributesParser.parseFilterAttributes(response)

        if (!this.filterAttributesParser.filterAttributeParseResultHasError(parsedAttributes)) {
          this.productsStore.setAttributes(this.attributes().merge(parsedAttributes))
        }
      }
    } finally {
      this.setSearchIsLoading(false)
    }
  }

  clearSelection = () => {
    this.gotoStep(0)

    const url: URL = new URL(this.treePath, window.location.href)
    url.searchParams.delete('v')

    this.treePath = url.toString()
    this.fetchFilters(url.toString())
    this.emitFilterEvent(url.toString())
  }

  search = (attribute: AttributeValue) => {
    const url: URL = new URL(this.treePath, window.location.href)

    const vParam = [
      PROPERTY_REF,
      attribute.attributeId,
      attribute.id,
    ].join('-')

    const urlAlreadyHasParam = url.searchParams.getAll('v').indexOf(vParam) !== -1

    if (urlAlreadyHasParam) {
      url.searchParams.delete('v', vParam)
    } else {
      url.searchParams.append('v', vParam)
    }

    this.treePath = url.toString()

    this.fetchFilters(url.toString())
    this.emitFilterEvent(url.toString())
  }

  private emitFilterEvent = (filterUrl: string) => {
    document.dispatchEvent(new PlpFiltersSelectedFiltersEvent([], filterUrl))
  }
}
